import React, { useContext, useEffect, useState } from 'react';
import useTranslationLgs from '@hooks/i18n/useTranslation.tsx';
import ButtonTag from '@components/framework/tags/ButtonTag.tsx';
import { SecuredConfig } from '@components/security/securedDecoration';
import { ApiContext } from '@api/api.tsx';
import { LovTransportationTypeOutDTO, TransportationTypeEnum } from '@api/ringil-api.v2.tsx';
import { Dropdown } from '@components/RingilComponents/SafeDropdown';
import { stampPrimeDropdownOverlayTestIds } from '@utils/testUtils';
import SvgTransportationTypeAirplane from '@components/framework/icons/TransportationTypes/TransportationTypeAirplane.tsx';
import SvgTransportationTypeBoat from '@components/framework/icons/TransportationTypes/TransportationTypeBoat.tsx';
import SvgTransportationTypeTrain from '@components/framework/icons/TransportationTypes/TransportationTypeTrain.tsx';
import SvgTransportationTypeTruck from '@components/framework/icons/TransportationTypes/TransportationTypeTruck.tsx';
import { getTransportIcon } from '@app/pages/carrier/demand-detail/carrierQuoteUtils.tsx';

interface ComponentProps {
  secured?: SecuredConfig;
  transportationType?: TransportationTypeEnum;
  readOnly?: boolean;
  required?: boolean;
  onChange: (code: TransportationTypeEnum) => void;
}

const TransportationTypeTag: React.FC<ComponentProps> = ({ secured, transportationType, readOnly, required, onChange }) => {
  const { tr } = useTranslationLgs();
  const [internalValue, setInternalValue] = useState<TransportationTypeEnum>(transportationType);

  useEffect(() => setInternalValue(transportationType), [transportationType]);

  const listTestId = 'expeditionInfo-modeOfTransportList';

  const applyOverlayTestIds = () => stampPrimeDropdownOverlayTestIds(listTestId);

  const { lovTransportationTypes } = useContext(ApiContext);
  const [transportationTypes, setTransportationTypes] = useState<LovTransportationTypeOutDTO[]>([]);

  useEffect(() => {
    lovTransportationTypes(d => {
      setTransportationTypes(d);

      // LOV options can arrive after the dropdown panel is already shown.
      // Re-stamp overlay + item testids so E2E can select options reliably.
      requestAnimationFrame(() => stampPrimeDropdownOverlayTestIds(listTestId));
    });
  }, []);

  const renderText = (code: string) => {
    const item = transportationTypes.find(i => i.transportationType === code);
    return item ? item.name : null;
  };

  return (
    <>
      <ButtonTag
        secured={secured}
        label={tr('TransportationTypeTag.typPrepravy', 'Typ přepravy')}
        required={required}
        readonly={readOnly}
        value={renderText(transportationType)}
        onSave={onSuccess => {
          onChange(internalValue);
          onSuccess();
        }}
        icon={getTransportIcon(transportationType)}
        autoClose
        dataTestId="expeditionInfo-modeOfTransport"
      >
        <div data-testid={listTestId}>
          <Dropdown
            className="w-full"
            options={transportationTypes.map(item => ({ code: item.transportationType, label: item.name }))}
            value={internalValue}
            optionLabel="label"
            optionValue={'code'}
            panelClassName="expeditionInfoDropdownPanel"
            onShow={() => {
              // PrimeReact can render panel and items in separate ticks.
              // Stamp in a later frame to catch the items.
              requestAnimationFrame(() => requestAnimationFrame(applyOverlayTestIds));
            }}
            onChange={e => {
              setInternalValue(e.target.value);
            }}
          />
        </div>
      </ButtonTag>
    </>
  );
};

export default TransportationTypeTag;
